// Map technical OAuth error messages to user-friendly descriptions.
// These messages are shown directly to creators, so keep them simple and actionable.
//
// More-specific patterns must appear before broad ones (e.g. "authorization
// code has been used" before the generic "exchange authorization code" catch-all).
export function getUserFriendlyOAuthError(message: string): string {
  const lower = message.toLowerCase();

  // ── Instagram-specific ────────────────────────────────────────────────────

  // Surfaced when the user's Instagram account isn't a Business/Creator
  // account linked to a Facebook Page (USER_INFO_FAILED cause message).
  if (lower.includes("no instagram business account")) {
    return "To connect your Instagram account, it must be set up as a Business or Creator account and linked to a Facebook Page. Please update your Instagram settings and try again.";
  }

  // ── Token-exchange errors ─────────────────────────────────────────────────

  // Facebook returns error_subcode 36009 when the same auth code is redeemed
  // twice. This is more specific than the generic exchange failure below.
  if (
    lower.includes("authorization code has been used") ||
    lower.includes("auth code has been used")
  ) {
    return "This connection link has already been used. Please go back and start the connection process again.";
  }

  // Generic token-exchange failure (network errors, expired codes, etc.).
  // Note: keep this after the "already been used" check above so the more
  // actionable message is shown when we have that detail.
  if (
    lower.includes("exchange authorization code") ||
    lower.includes("token exchange failed") ||
    lower.includes("failed to exchange")
  ) {
    return "Something went wrong while connecting your account. Please go back and try again. If the problem persists, wait a moment and try once more.";
  }

  // ── User-info errors ──────────────────────────────────────────────────────

  // Generic failure fetching account details from the social platform after
  // a successful token exchange (e.g., API unavailable, missing permissions).
  if (
    lower.includes("failed to fetch user info") ||
    lower.includes("user_info_failed") ||
    lower.includes("user info failed")
  ) {
    return "We couldn't retrieve your social account details. Please go back and try connecting again. Make sure the required permissions are granted when prompted.";
  }

  // ── Session / state errors ────────────────────────────────────────────────

  if (lower.includes("state expired") || lower.includes("invalid state")) {
    return "This connection request has expired. Please go back and try connecting your account again.";
  }

  // ── Provider / config errors ──────────────────────────────────────────────

  if (lower.includes("not configured")) {
    return "This social platform isn't available for connecting right now. Please contact support if you need help.";
  }

  // ── Provider-returned errors (in the callback URL) ────────────────────────

  if (lower.includes("access_denied")) {
    return "It looks like access was not granted on the social platform. To connect your account, you'll need to approve the permissions when prompted.";
  }

  // ── Misc ──────────────────────────────────────────────────────────────────

  if (lower.includes("organization mismatch")) {
    return "Something went wrong with your session. Please go back and try connecting your account again.";
  }

  return message;
}
